/**
 * The conversation chat's per-message renderers for the thread (`ConversationThread`): what a row draws itself
 * (`renderMessage`: close-summary, workflow draft, clarification, drafter tool card, the raw view), what sits under a
 * row (`renderMessageFooter`: intent badge, tokens, raw-view toggle) and what sits beside a live tool call
 * (`renderToolCallExtra`: panel link, duration). Demo page: /chats.
 */
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { listConversationIntentsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { IntentClassificationDecisionPayload } from '../../generated/api/types.gen.js';
import type { ConversationEventsState } from '../../hooks/useConversationEvents.js';
import type { StreamMessage } from '../../hooks/useConversationStream.js';
import { CloseSummaryCard, parseCloseSummary } from '../context-panel/CloseSummaryCard.js';
import { parsePanelUrl, useContextPanel } from '../context-panel/url-state.js';
import { Button } from '../ui/Button.js';
import { DisambiguationPills, parseAmbiguousMessage } from './DisambiguationPills.js';
import { IntentBadge } from './IntentBadge.js';
import { MessageAuthorLabel } from './MessageAuthorLabel.js';
import { MessageRecipients } from './MessageRecipients.js';
import { RawMessageButton, RawMessageView } from './RawMessageToggle.js';
import { ToolCallCard, isRenderableToolCall } from './ToolCallCard.js';
import { WorkflowDraftCard, parseWorkflowDraftMessage } from './WorkflowDraftCard.js';

type MessageRef = { id: string; role: 'user' | 'assistant' | 'system' };

/** Total tokens across every provider/model that contributed to one message; `null` when none are recorded. */
export function totalTokens(counts: StreamMessage['tokenCounts']): number | null {
  if (!counts || counts.length === 0) return null;
  const total = counts.reduce((sum, entry) => sum + (Number.isFinite(entry.count) ? entry.count : 0), 0);
  return total > 0 ? total : null;
}

/** The intent decision of each user message, and the ambiguous ones still waiting for a choice. */
export function indexIntents(rows: readonly IntentClassificationDecisionPayload[]) {
  const byUserMessage = new Map<string, IntentClassificationDecisionPayload>();
  const openAmbiguous = new Map<string, IntentClassificationDecisionPayload>();
  const resolvedFrom = new Set<number>();
  for (const row of rows) {
    if (row.disambiguatedFromDecisionId !== null) resolvedFrom.add(row.disambiguatedFromDecisionId);
    const key = String(row.messageId);
    if (!byUserMessage.has(key)) byUserMessage.set(key, row);
  }
  for (const row of rows) {
    if (row.classification.kind !== 'ambiguous' || resolvedFrom.has(row.decisionId)) continue;
    const key = String(row.messageId);
    if (!openAmbiguous.has(key)) openAmbiguous.set(key, row);
  }
  return { byUserMessage, openAmbiguous };
}

/** "View →" for a live tool call that produced a context panel. */
function PanelLink({ panelLink, toolName }: { panelLink: string; toolName: string }) {
  const { openPanel } = useContextPanel();
  const pointer = parsePanelUrl(panelLink.startsWith('?panel=') ? panelLink.slice('?panel='.length) : panelLink);
  if (!pointer) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-auto p-0 text-primary-500 hover:underline"
      aria-label={`View ${toolName} results in panel`}
      onClick={(event) => {
        // The row is a <summary>: pressing the link must not fold it.
        event.preventDefault();
        event.stopPropagation();
        openPanel(pointer);
      }}
    >
      View →
    </Button>
  );
}

interface ConversationRendererOptions {
  conversationId: string | null;
  surface: 'simple' | 'detailed';
  /** Every stored row of the stream (the thread may draw fewer). */
  messages: readonly StreamMessage[];
  tools: ConversationEventsState['orchestratorTurn']['activeToolCalls'];
  /** The person looking at the conversation (`users.id`): their own messages are drawn as theirs. */
  currentUserId: string | null;
}

/** The thread renderers of a conversation, and the raw-view state they share. */
export function useConversationRenderers({
  conversationId,
  surface,
  messages,
  tools,
  currentUserId,
}: ConversationRendererOptions) {
  const [rawIds, setRawIds] = useState<ReadonlySet<string>>(new Set());
  const detailed = surface === 'detailed';

  const intents = useQuery({
    ...listConversationIntentsOptions({ path: { id: conversationId ?? '' }, query: { limit: 100 } }),
    enabled: Boolean(conversationId) && detailed,
  });
  const intentRows = intents.data?.data;
  const index = useMemo(() => indexIntents(intentRows ?? []), [intentRows]);
  const byId = useMemo(
    () => new Map(messages.map((message, position) => [message.id, { message, position }])),
    [messages],
  );

  const toggleRaw = (id: string) =>
    setRawIds((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const renderMessage = (ref: MessageRef): ReactNode | null => {
    const entry = byId.get(ref.id);
    if (!entry || !conversationId) return null;
    const { message, position } = entry;
    if (rawIds.has(message.id) && message.role !== 'user') {
      return <RawMessageView content={message.content} parts={message.parts} />;
    }
    if (message.role === 'tool' && !message.toolCalls?.length) {
      return isRenderableToolCall(message.content) ? <ToolCallCard message={message} /> : null;
    }
    if (message.role !== 'system' || (message.parts?.length ?? 0) > 0) return null;

    const closeSummary = parseCloseSummary(message.content);
    if (closeSummary) return <CloseSummaryCard {...closeSummary} />;
    const draft = parseWorkflowDraftMessage(message.content);
    if (draft) return <WorkflowDraftCard id={draft.id} name={draft.name} />;
    const ambiguous = parseAmbiguousMessage(message.content);
    if (ambiguous) {
      let priorUserId: string | null = null;
      for (let i = position - 1; i >= 0; i -= 1) {
        const previous = messages[i];
        if (previous?.role === 'user') {
          priorUserId = previous.id;
          break;
        }
      }
      const open = priorUserId ? index.openAmbiguous.get(priorUserId) : undefined;
      return open ? (
        <DisambiguationPills
          conversationId={conversationId}
          decisionId={String(open.decisionId)}
          parsed={ambiguous}
          surface={surface}
        />
      ) : intents.isError ? (
        // The choices come from the intents query; when it failed, say so rather than show a question with no answers.
        <div className="flex flex-wrap items-center gap-2 text-sm text-text-tertiary">
          <span className="italic">{ambiguous.clarificationPrompt}</span>
          <Button variant="ghost" size="sm" onClick={() => void intents.refetch()}>
            The choices did not load. Try again
          </Button>
        </div>
      ) : (
        <p className="text-sm text-text-tertiary italic">{ambiguous.clarificationPrompt}</p>
      );
    }
    return null;
  };

  /** A person's message written by someone else: another person, or an external agent writing as a member. */
  const isIncoming = (ref: MessageRef): boolean => {
    const author = byId.get(ref.id)?.message.author;
    if (ref.role !== 'user' || !author) return false;
    return !(author.memberType === 'user' && currentUserId !== null && String(author.memberId) === currentUserId);
  };

  /** Who wrote a member's message: every AI member's and system line's author, and another person's name. */
  const renderMessageHeader = (ref: MessageRef): ReactNode | null => {
    const author = byId.get(ref.id)?.message.author;
    if (!author) return null;
    if (ref.role === 'user' && !isIncoming(ref)) return null;
    return <MessageAuthorLabel author={author} />;
  };

  const renderMessageFooter = (ref: MessageRef): ReactNode => {
    const entry = byId.get(ref.id);
    if (!entry) return null;
    const { message } = entry;
    const tokens = totalTokens(message.tokenCounts);
    const intent = ref.role === 'user' ? index.byUserMessage.get(message.id) : undefined;
    // The raw view of something a person typed is the text they typed: no toggle on their own messages.
    const rawToggle = ref.role !== 'user' && detailed;
    const to = message.to ?? [];
    if (!intent && tokens === null && !rawToggle && to.length === 0) return null;
    return (
      <>
        <MessageRecipients to={to} me={currentUserId} />
        {intent && (
          <IntentBadge
            kind={intent.classification.kind}
            title={`${intent.classification.rationale} · ${Math.round(intent.classification.confidence * 100)}%`}
          />
        )}
        {tokens !== null && <span className="font-mono text-[10px] text-text-tertiary">{tokens} tokens</span>}
        {rawToggle && <RawMessageButton showRaw={rawIds.has(message.id)} onToggle={() => toggleRaw(message.id)} />}
      </>
    );
  };

  const renderToolCallExtra = (toolCallId: string, toolName: string): ReactNode => {
    const call = tools.get(toolCallId);
    if (call?.status !== 'completed') return null;
    return (
      <>
        {call.durationMs !== undefined && <span className="text-xs text-text-tertiary">{call.durationMs} ms</span>}
        {call.panelLink && <PanelLink panelLink={call.panelLink} toolName={toolName} />}
      </>
    );
  };

  return { renderMessage, renderMessageFooter, renderMessageHeader, isIncoming, renderToolCallExtra };
}
